import { Box, Typography, useTheme } from "@mui/material"
import dayjs from "dayjs"
import numbro from "numbro"
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts"

import useDailyVolume from "../../hooks/use-daily-volume"

export default function ProtocolVolumeChart() {
  const theme = useTheme()
  const { data, isLoading } = useDailyVolume()

  if (isLoading || !data) {
    return null
  }

  // Order data by date asc
  const orderedDataDict: Record<string, { daily_volume_usd: number; date: string }> = {} //data.sort((a, b) => a.date.localeCompare(b.date))
  data.forEach((item) => {
    const previousValue = orderedDataDict[item.date]?.daily_volume_usd || 0
    orderedDataDict[item.date] = {
      daily_volume_usd: item.daily_volume_usd + previousValue,
      date: item.date,
    }
  })
  const orderedData = Object.values(orderedDataDict).sort((a, b) => a.date.localeCompare(b.date))

  // Calculate total volume in the past month
  const totalVolume = orderedData.reduce((acc, curr) => acc + curr.daily_volume_usd, 0)

  // Custom formatter for tooltip
  const customTooltipFormatter = (
    value: number,
    _name: string,
    _entry: { payload?: { date?: string } },
  ): [string, string] => {
    return [
      `${numbro(value).formatCurrency({ thousandSeparated: true, mantissa: 2, currencySymbol: "$" })}`,
      `Daily volume`,
    ]
  }

  // Custom formatter for X-axis ticks
  const formatXAxis = (tickItem: string) => {
    return dayjs(tickItem).format("D MMM YYYY")
  }

  return (
    <div
      style={{
        paddingBottom: "1rem",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box>
        <Typography>Botega Protocol Volume</Typography>

        <Typography
          sx={{
            fontSize: "3rem",
            fontWeight: 400,
          }}
        >
          ${numbro(totalVolume).format({ thousandSeparated: true, average: true, mantissa: 2 })}
        </Typography>

        <Typography>Past Month</Typography>
      </Box>
      <Box height={250}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={orderedData}>
            <XAxis
              dataKey="date"
              interval="preserveStartEnd"
              minTickGap={200}
              tickFormatter={formatXAxis}
            />
            <Tooltip formatter={customTooltipFormatter} labelFormatter={formatXAxis} />
            <Bar
              dataKey="daily_volume_usd"
              fill={theme.palette.accent.main}
              radius={[12, 12, 0, 0]}
              barSize="28"
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </div>
  )
}
